import { Tabs } from 'antd';
import { useState } from 'react';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { VehicleRigPanel } from '../../../components/VehicleRigPanel';
import { UNITS_TRAIL } from '../../units/components/units-trail';
import { SPARE_TRAILER_TABS, unitPath, type SpareTrailerTab } from '../../units/units.paths';
import { useTrailerDetail } from '../hooks/use-trailer-detail';
import { useTrailerPermissions } from '../hooks/use-trailer-permissions';
import { buildTrailerDetailTabs } from './build-trailer-detail-tabs';
import { TrailerStatusControl } from './TrailerStatusControl';

interface TrailerDetailProps {
  trailerId: string;
  tab: SpareTrailerTab;
  /** After it is coupled to a truck: it then belongs to that truck's unit. */
  onCoupled: () => void;
}

/**
 * A spare trailer's page, at its own registration (gitaumoses4/chukua#390): a trailer on no truck isn't part of a unit, so it
 * keeps its own tabs. `fleet.view` reads; `truck.manage` mutates the record and couples it, `document.manage` its documents
 * (gitaumoses4/chukua#13, #15, #17). Once coupled, its address opens the truck's unit.
 */
export function TrailerDetail({ trailerId, tab, onCoupled }: TrailerDetailProps) {
  const navigate = useNavigate();
  const detail = useTrailerDetail(trailerId);
  const { state } = detail;
  const { isKnown, canManage, canManageDocuments, canViewMaintenance } = useTrailerPermissions();
  const [couplingVersion, setCouplingVersion] = useState(0);

  if (tab === 'tyres' && isKnown && !canViewMaintenance && state.status === 'loaded') {
    return <Navigate to={unitPath(state.trailer.registration, 'details')} replace />;
  }
  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading trailer" breadcrumbs={UNITS_TRAIL} tabs={SPARE_TRAILER_TABS.length} />;
  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Trailer not found" description="This trailer doesn't exist, or the link is out of date." />;
  }
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this trailer" description="Something went wrong loading this trailer. Try reloading the page." />
    );
  }

  const { trailer } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={UNITS_TRAIL}
        title={trailer.registration}
        description={`Spare trailer · ${trailer.typeCode}`}
        actions={<TrailerStatusControl trailer={trailer} canManage={canManage} onActivate={detail.activate} onDeactivate={detail.deactivate} />}
      />
      {/* Keyed on the record's last change, so a new type or colour redraws it. */}
      <VehicleRigPanel
        key={trailer.updatedAt}
        vehicleKind="trailer"
        vehicleId={trailer.id}
        canManage={canManage}
        onChanged={() => {
          setCouplingVersion((version) => version + 1);
          onCoupled();
        }}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(unitPath(trailer.registration, key as SpareTrailerTab))}
        items={buildTrailerDetailTabs({ trailer, canManage, canManageDocuments, canViewMaintenance, detail, couplingVersion })}
      />
    </>
  );
}
